import type { BadgeTone } from '@/lib/utils/badgeTone'

import type {
  ReservationCancelInput,
  ReservationCancelReason,
  ReservationCancelRefundStatus,
} from './reservationCancelTypes'
import type { VenueReservationStatus } from './reservationTypes'

/**
 * 예약 취소 모달 폼 상태·검증·변환(#672 §2-3 (c) 관리자 요청 바디). `reservationRescheduleViewModel.ts`와
 * 같은 명명 관례를 따른다.
 *
 * 🚨 사유 코드 정합성(어떤 사유가 취소 주체를 어떻게 바꾸는지)·환불 비율 계산 등 서버 전용 규칙은 여기서
 * 재검증하지 않는다 — 이 파일은 상세사유 길이 등 최소한의 클라이언트 사전 검증만 담당한다.
 */

/** 관리자 표면이 허용하는 취소 사유 2값(#672 §2-3 (c) — 그 외 값은 서버가 400으로 거부). */
export const RESERVATION_CANCEL_REASON_OPTIONS: { value: ReservationCancelReason; label: string }[] = [
  { value: 'VENUE_CIRCUMSTANCE', label: '구장 사정' },
  { value: 'USER_REQUEST', label: '예약자 요청(전화 등)' },
]

export const MAX_CANCEL_REASON_DETAIL_LENGTH = 200

export interface CancelFormState {
  reason: ReservationCancelReason
  reasonDetail: string
}

/** 기본 사유는 "구장 사정" — 관리자가 직접 처리하는 가장 흔한 경로. */
export function initialCancelFormState(): CancelFormState {
  return { reason: 'VENUE_CIRCUMSTANCE', reasonDetail: '' }
}

export interface CancelFormErrors {
  reasonDetail?: string
}

export function hasCancelFormErrors(errors: CancelFormErrors): boolean {
  return Object.keys(errors).length > 0
}

export function validateCancelForm(form: CancelFormState): CancelFormErrors {
  const errors: CancelFormErrors = {}
  if (form.reasonDetail.trim().length > MAX_CANCEL_REASON_DETAIL_LENGTH) {
    errors.reasonDetail = `상세 사유는 최대 ${MAX_CANCEL_REASON_DETAIL_LENGTH}자까지 입력할 수 있습니다.`
  }
  return errors
}

/** 🚨 `reasonDetail`이 선택이므로 빈 문자열은 `undefined`로 보내 서버 바디를 최소화한다. */
export function toCancelInput(form: CancelFormState): ReservationCancelInput {
  const trimmed = form.reasonDetail.trim()
  return { reason: form.reason, reasonDetail: trimmed.length > 0 ? trimmed : undefined }
}

/**
 * 두 취소 입력이 "같은 시도"인가 — 멱등키 재사용 판정에 쓴다(#672 §2-4 (2)). 서버가 "같은 키에 다른
 * 바디"를 400으로 막으므로, 사유를 고쳐 쓰고 재시도하면 이 함수가 false를 돌려줘 호출부가 새 키를
 * 발급하게 한다. 네트워크 실패 등으로 값 그대로 재시도하면 true라 같은 키를 재사용해 이중 환불을 막는다.
 */
export function sameCancelInput(a: ReservationCancelInput, b: ReservationCancelInput): boolean {
  return a.reason === b.reason && (a.reasonDetail ?? '') === (b.reasonDetail ?? '')
}

/** "예약 취소 처리" 버튼 활성 조건 — #672 §B ②의 유일한 상태 게이트(고정 enum 비교, 정책 계산 아님). */
export function isReservationCancelable(status: VenueReservationStatus): boolean {
  return status === 'CONFIRMED'
}

/**
 * 취소 결과 화면의 환불 배지 색(텍스트는 항상 서버 `statusLabel`) — 기존 `Badge`의 4톤(`BadgeTone`) 안에서
 * 매핑한다(`RESERVATION_STATUS_TONE`와 같은 관례).
 */
export const REFUND_STATUS_TONE: Record<ReservationCancelRefundStatus, BadgeTone> = {
  REQUESTED: 'warning',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
}
